import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import api from '../utils/api';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronDown, SlidersHorizontal, X } from 'lucide-react';

const ProductListing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterValues, setFilterValues] = useState({ colors: [], fabrics: [], occasions: [] });
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const category = searchParams.get('category');
  const color = searchParams.get('color');
  const fabric = searchParams.get('fabric');
  const occasion = searchParams.get('occasion');
  const minPrice = searchParams.get('min_price');
  const maxPrice = searchParams.get('max_price');
  const searchQuery = searchParams.get('q');

  const activeFilterCount = [color, fabric, occasion, minPrice, maxPrice].filter(Boolean).length;
  const [localMin, setLocalMin] = useState(minPrice || '');
  const [localMax, setLocalMax] = useState(maxPrice || '');

  useEffect(() => {
    fetchProducts();
    api.get('/banners?page=products').then(r => setBanners(r.data || [])).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data || [])).catch(() => {});
    api.get('/products/filter-values').then(r => setFilterValues(r.data)).catch(() => {});
  }, [category, color, fabric, occasion, minPrice, maxPrice, searchQuery]);

  useEffect(() => { setLocalMin(minPrice || ''); }, [minPrice]);
  useEffect(() => { setLocalMax(maxPrice || ''); }, [maxPrice]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (category) params.category = category;
      if (color) params.color = color;
      if (fabric) params.fabric = fabric;
      if (occasion) params.occasion = occasion;
      if (minPrice) params.min_price = minPrice;
      if (maxPrice) params.max_price = maxPrice;
      if (searchQuery) params.q = searchQuery;
      const response = await api.get('/products', { params });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    const cat = searchParams.get('category');
    const q = searchParams.get('q');
    const newParams = {};
    if (cat) newParams.category = cat;
    if (q) newParams.q = q;
    setSearchParams(newParams);
  };

  const FilterContent = () => (
    <>
      <div className="mb-4 lg:mb-6">
        <label className="text-xs uppercase tracking-[0.1em] text-forest mb-1.5 block">Category</label>
        <select value={category || ''} onChange={(e) => updateFilter('category', e.target.value)}
          className="w-full px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-forest"
          data-testid="category-filter">
          <option value="">All</option>
          {categories.map(c => <option key={c.category_id} value={c.slug}>{c.name}</option>)}
        </select>
      </div>
      <div className="mb-4 lg:mb-6">
        <label className="text-xs uppercase tracking-[0.1em] text-forest mb-1.5 block">Color</label>
        <select value={color || ''} onChange={(e) => updateFilter('color', e.target.value)}
          className="w-full px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-forest"
          data-testid="color-filter">
          <option value="">All</option>
          {filterValues.colors.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="mb-4 lg:mb-6">
        <label className="text-xs uppercase tracking-[0.1em] text-forest mb-1.5 block">Fabric</label>
        <select value={fabric || ''} onChange={(e) => updateFilter('fabric', e.target.value)}
          className="w-full px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-forest"
          data-testid="fabric-filter">
          <option value="">All</option>
          {filterValues.fabrics.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>
      <div className="mb-4 lg:mb-6">
        <label className="text-xs uppercase tracking-[0.1em] text-forest mb-1.5 block">Occasion</label>
        <select value={occasion || ''} onChange={(e) => updateFilter('occasion', e.target.value)}
          className="w-full px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-forest"
          data-testid="occasion-filter">
          <option value="">All</option>
          {filterValues.occasions.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div className="mb-4 lg:mb-6">
        <label className="text-xs uppercase tracking-[0.1em] text-forest mb-1.5 block">Price</label>
        <div className="flex gap-2">
          <input type="number" placeholder="Min" value={localMin} onChange={(e) => setLocalMin(e.target.value)}
            onBlur={() => updateFilter('min_price', localMin)}
            onKeyDown={(e) => { if (e.key === 'Enter') updateFilter('min_price', localMin); }}
            className="w-full px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-forest" data-testid="min-price-filter" />
          <input type="number" placeholder="Max" value={localMax} onChange={(e) => setLocalMax(e.target.value)}
            onBlur={() => updateFilter('max_price', localMax)}
            onKeyDown={(e) => { if (e.key === 'Enter') updateFilter('max_price', localMax); }}
            className="w-full px-3 py-2 bg-ivory border border-gold/50 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-forest" data-testid="max-price-filter" />
        </div>
      </div>
      {activeFilterCount > 0 && (
        <button onClick={clearFilters}
          className="w-full border border-forest text-forest px-4 py-2 text-xs uppercase tracking-[0.1em] hover:bg-forest hover:text-ivory transition-colors"
          data-testid="clear-filters-button">
          Clear Filters
        </button>
      )}
    </>
  );

  return (
    <div className="min-h-screen" data-testid="product-listing-page">
      {banners.length > 0 && banners.map(b => (
        <Link key={b.banner_id} to={b.link_url || '#'} className="block">
          <div className="py-2.5 px-4 text-center" style={{ backgroundColor: b.bg_color, color: b.text_color }}>
            <span className="text-sm font-medium tracking-wide">{b.title}</span>
            {b.subtitle && <span className="text-sm ml-2 opacity-90">| {b.subtitle}</span>}
            <ChevronRight className="w-4 h-4 inline-block ml-1 opacity-70" />
          </div>
        </Link>
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
        {/* Header */}
        <div className="mb-4 lg:mb-10">
          <h1 className="font-heading text-3xl lg:text-5xl text-forest mb-1 tracking-tight" data-testid="page-title">
            {searchQuery ? `Search: "${searchQuery}"` : categories.find(c => c.slug === category)?.name || (category ? category.charAt(0).toUpperCase() + category.slice(1) + 's' : 'All Products')}
          </h1>
          <p className="text-muted-foreground text-sm">{products.length} products found</p>
        </div>

        {/* Mobile Filter Bar */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="w-full flex items-center justify-between px-4 py-3 bg-neutral-warm border border-gold/30 text-sm text-forest"
            data-testid="mobile-filter-toggle"
          >
            <span className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              <span className="uppercase tracking-[0.1em] text-xs font-medium">Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-forest text-ivory text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{activeFilterCount}</span>
              )}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`} />
          </button>
          {showMobileFilters && (
            <div className="bg-neutral-warm border border-t-0 border-gold/30 px-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <FilterContent />
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Desktop Filters */}
          <div className="hidden lg:block" data-testid="filters-section">
            <div className="bg-neutral-warm p-6 rounded-sm sticky top-24">
              <h3 className="text-sm uppercase tracking-[0.2em] text-forest mb-4">Filters</h3>
              <FilterContent />
            </div>
          </div>

          {/* Products Grid — 2 cols on mobile, 3 on desktop */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12"><p className="text-muted-foreground">Loading...</p></div>
            ) : products.length === 0 ? (
              <div className="text-center py-12"><p className="text-muted-foreground">No products found</p></div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-8" data-testid="products-grid">
                {products.map((product, index) => (
                  <motion.div key={product.product_id}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.5) }}>
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductListing;
